import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { CONSULTANT_VIEW } from "@platform/feature-flags";
import { Autocomplete, Box, Stack } from "@mui/material";
import { Alert, Button, TextField, Typography } from "@platform/design-system";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { useApi } from "@platform/api";

import { Loader } from "../../components";
import { ROUTE_NAMES } from "../../constants";
import { extractErrorMessage } from "../../utils";

import { ConsultingRfpTimeline } from "./ConsultingRfpTimeline";
import { PreferredCarrierZones } from "./PreferredCarrierZones";
import { pageContainerErrorSx, pageContainerSx } from "./styles";
import { useConsultingStewardshipSummaryQuery } from "./useBrokerStewardship";

import type { ConsultingBroker } from "./types";

function ConsultingHome(): React.JSX.Element {
  const axiosInstance = useApi();
  const navigate = useNavigate();
  const consultantViewEnabled = useFeatureIsOn(CONSULTANT_VIEW);
  const [selectedBroker, setSelectedBroker] = useState<ConsultingBroker | null>(
    null,
  );

  const stewardshipSummaryQuery = useConsultingStewardshipSummaryQuery({
    enabled: consultantViewEnabled,
  });

  const brokersQuery = useQuery<ConsultingBroker[]>({
    queryKey: ["consulting", "brokers"],
    queryFn: () =>
      axiosInstance
        .get<ConsultingBroker[]>("/consulting/v1/brokers")
        .then((resp) => resp.data),
  });

  const handleSelect = useCallback(() => {
    if (!selectedBroker) {
      return;
    }
    navigate(`/${ROUTE_NAMES.CONSULTING}/${selectedBroker.id}`);
  }, [navigate, selectedBroker]);

  if (brokersQuery.isError) {
    return (
      <Box data-testid="consulting-home" sx={pageContainerErrorSx}>
        <Alert severity="error">
          {extractErrorMessage(brokersQuery.error)}
        </Alert>
      </Box>
    );
  }

  if (brokersQuery.isLoading) {
    return <Loader />;
  }

  const brokers = brokersQuery.data ?? [];

  if (brokers.length === 0) {
    return (
      <Box data-testid="consulting-home" sx={pageContainerSx}>
        <Typography variant="display-medium" mb={2}>
          Consulting Portal
        </Typography>
        <Typography>No brokers are available yet.</Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="consulting-home" sx={pageContainerSx}>
      <Typography variant="display-medium" mb={2}>
        Consulting Portal
      </Typography>
      <Stack spacing={2} flex={1} width="100%">
        <Typography variant="subtitle2" color="textSecondary">
          Select a Broker to continue
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Autocomplete
            data-testid="broker-autocomplete"
            sx={{ width: 300 }}
            options={brokers}
            value={selectedBroker}
            onChange={(_event, newValue) => setSelectedBroker(newValue)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} size="sm" label="Broker" />
            )}
          />
          <Button
            size="lg"
            variant="primary"
            data-testid="broker-select-button"
            onClick={handleSelect}
            disabled={!selectedBroker}
          >
            Select
          </Button>
        </Stack>
      </Stack>

      {consultantViewEnabled ? (
        <Stack spacing={3} mt={3}>
          <PreferredCarrierZones
            metrics={stewardshipSummaryQuery.data?.metrics}
            isLoading={stewardshipSummaryQuery.isLoading}
            isError={stewardshipSummaryQuery.isError}
            error={stewardshipSummaryQuery.error}
          />
          <ConsultingRfpTimeline showBrokerName enabled />
        </Stack>
      ) : null}
    </Box>
  );
}

export { ConsultingHome };
